import type { CSSProperties } from 'react';

import {
  generateGridTemplateColumns,
  generateGridTemplateAreas,
} from './layout-styles.js';
import { getNextPanelIndex } from './panels.js';
import {
  vwToPx,
  remToPx,
  pxToFractions,
  parseValue,
  ParsedValue,
  panelPropWidthToPx,
  fractionsToPx,
  anyUnitToPx,
  anyUnitToFractions,
} from './units.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { RESIZE_HANDLE_SIZE } from '../Resizer.js';
import {
  ResizeHandles,
  PanelSizesWithUnit,
  PanelChildren,
} from '../types/shared-panel-types.js';

const PRECISION = 2;

const MAX_RECURSIONS = 3;
/**
 * Get the initial styles for the root panel to ensure correct sizing and placement
 * of the registered children and resize handles.
 * @param rootWidth - Current width of the parent panel.
 * @param panels - All the registered child panels, sorted by their order.
 * @param resizeHandles - All the registered resize handles, sorted by their order.
 * @param useMinWidthPanels - An array of panel names where the initial width would be less than the panels minWidth. For these panels, we use the minWidth to recalculate the initial width of the panel.
 * @param recursions - Maximum number of recursions to check if all panels fit in the container.
 * @returns The grid template areas corresponding to the panels / resize handles and the template columns.
/*

Example:
Container Size: 1000px
Panel1 - minWidth: 500px
Panel2 - minWidth: 300px
Panel3 - no config

Recursion   || Sizes                                           || useMinWidth       || Panels fit container
            || Panel1        | Panel2         | Panel3         ||                   ||
-----------------------------------------------------------------------------------------------
0           || 33.33 = 328px | 33.33 = 328px  | 33.33 = 328px  || [Panel1]          || false
1           || 50.81 = 500px | 24.59 = 242px  | 24.59 = 242px  || [Panel1, Panel3]  || false
2           || 50.81 = 500px | 18.70 = 184px  | 30.49 = 300px  || [Panel1, Panel3]  || true

*/
export function getInitialGridStylesAndSizes(
  rootWidth: number,
  panels: PanelChildren,
  resizeHandles: ResizeHandles,
  useMinWidthPanels?: string[],
  recursions?: number,
): {
  gridTemplateStyles: CSSProperties;
  initialPanelSizes: PanelSizesWithUnit;
} {
  const initialPanelSizes: PanelSizesWithUnit = {};

  // Create template areas for all panels and resize handles.
  const templateAreas = generateGridTemplateAreas(panels, resizeHandles);

  // Calculate the total preferred width of all panels and the number of panels
  // that don't have a preferred width set. Those will share the remaining space.
  let panelsWithoutPreferredWidth = 0;
  const [totalWidth, totalMinWidth] = panels.reduce(
    ([totalWidth, minWidth]: [number, number], panel) => {
      const panelMinWidth = panel.minWidth || 0;
      const totalMinWidth = minWidth + panelMinWidth;

      // We already tried to fit the panel with its preferred width or the evenly distributed
      // remaining width, but it didn't fit. In this case try the min width.
      if (useMinWidthPanels?.includes(panel.name)) {
        return [totalWidth + panelMinWidth, totalMinWidth];
      }

      if (panel.preferredWidth) {
        const targetPanelWidth = panelPropWidthToPx(
          panel.preferredWidth,
          rootWidth,
          resizeHandles.length,
        );

        if (!targetPanelWidth) {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.warn(
              `The preferred width of panel '${panel.name}' is not supported and will be ignored.`,
            );
          }
        } else {
          return [totalWidth + targetPanelWidth, totalMinWidth];
        }
      }

      panelsWithoutPreferredWidth += 1;
      return [totalWidth, totalMinWidth];
    },
    [0, 0],
  );

  if (totalMinWidth > rootWidth) {
    // eslint-disable-next-line no-console
    console.warn(
      'The combined minWidth of all panels exceeds the container width. The panels might not work as expected. Consider adjusting your panel configuration.',
    );
  }

  // Total amount of fractions of all panels (regardless of whether they are resizable)
  let totalFractions = 0;
  // Total amount of fractions of all resizable panels. They may take up leftover space if the configured
  // panels don't fill out the whole parent panel.
  let totalResizablePanelsFractions = 0;
  // Total amount of fractions of all non-resizable panels
  let totalNonResizablePanelsFractions = 0;

  // Calculate initial panel sizes.
  for (const panel of panels) {
    const targetWidth = useMinWidthPanels?.includes(panel.name)
      ? panel.minWidth
      : panel.preferredWidth;
    const parsedValue = parseValue(targetWidth);
    const { value, unit } = parsedValue || { unit: 'px' };

    // Set the initial size of non-resizable panels to the given value and unit.  % and fr are handled as if the panel was
    // resizable to avoid having too many fractions in the end.
    // if the unit is in %, we want to convert it to fr to prevent issues with 100% and be consistent
    // we always add up fr to a total of around 100fr, so % and fr can be treated the same way, but we prefer fr because they're more robust when it comes to rounding issues
    if (
      !panel.resizable &&
      parsedValue &&
      value !== undefined &&
      unit !== undefined &&
      unit !== '%' &&
      unit !== 'fr'
    ) {
      const panelFractions =
        anyUnitToFractions(parsedValue, rootWidth, resizeHandles.length) || 0;
      totalFractions += panelFractions;
      totalNonResizablePanelsFractions += panelFractions;

      initialPanelSizes[panel.name] = {
        value,
        unit,
      };

      continue;
    }

    let panelFractions = 0;

    if (value === undefined || (value === 100 && unit === '%')) {
      // Distribute the space that is left after subtracting all the preferred widths
      // evenly among panels that don't have a preferred width set.
      const leftoverWidth =
        rootWidth - RESIZE_HANDLE_SIZE * resizeHandles.length - totalWidth;
      panelFractions = pxToFractions(
        leftoverWidth / panelsWithoutPreferredWidth,
        rootWidth,
        resizeHandles.length,
      );
    } else {
      switch (unit) {
        case 'fr':
        case '%':
          panelFractions = value;
          break;
        case 'rem':
          panelFractions = pxToFractions(
            remToPx(value),
            rootWidth,
            resizeHandles.length,
          );
          break;
        case 'vw':
          panelFractions = pxToFractions(
            vwToPx(value),
            rootWidth,
            resizeHandles.length,
          );
          break;
        default:
          // Percentage of preferred width in pixels of the full available width.
          // This will ensure that if a preferredWidth is set, the initial size will
          // match the preferred width.
          panelFractions = pxToFractions(
            value,
            rootWidth,
            resizeHandles.length,
          );
      }
    }

    // Depending on the minWidths of the panels, the fractions might be negative for some
    // panels. This would break the grid, so we clamp to 0.
    // e.g.: 3 panels, screen width = 1000
    //       totalPreferredWidth = 1200 px
    //       leftoverWidth would be -216px -> panelFractions = -21.95
    panelFractions = Math.max(panelFractions, 0);
    totalResizablePanelsFractions += panelFractions;
    totalFractions += panelFractions;

    initialPanelSizes[panel.name] = {
      value: panelFractions,
      unit: 'fr',
    };
  }

  // Check if all panels have at least their minWidth with the initially calculated sizes.
  // With e.g. percentage as preferredWidth, they might be smaller. If so, we fall back to the minWidth.
  if ((recursions ?? 0) < MAX_RECURSIONS) {
    // Array of panels that don't fit in the parent panel with their applied config.
    const panelsLessThanMinWidth: string[] = useMinWidthPanels || [];
    // Whether all panels fit in the parent panel.
    const panelsFit = panels.reduce((acc, panel) => {
      const targetWidth =
        anyUnitToPx(
          initialPanelSizes[panel.name],
          rootWidth,
          resizeHandles.length,
        ) ?? 0;

      if (
        targetWidth < (panel.minWidth || 0) &&
        !panelsLessThanMinWidth.includes(panel.name)
      ) {
        panelsLessThanMinWidth.push(panel.name);
        return false;
      }

      return acc;
    }, true);

    // Re-adjust sizes due to panels not having their minWidth after initializing.
    if (!panelsFit) {
      return getInitialGridStylesAndSizes(
        rootWidth,
        panels,
        resizeHandles,
        panelsLessThanMinWidth,
        (recursions ?? 0) + 1,
      );
    }
  }

  // If the total fractions exceed 100 when initializing, the combined min / preferred widths
  // of the panels exceed the parent panel container width and we need to decrease all fractions
  // to get to a total of 100.
  if (totalFractions > 100) {
    for (const [key, { value, unit }] of Object.entries(initialPanelSizes)) {
      if (unit !== 'fr') {
        continue;
      }

      initialPanelSizes[key].value =
        Math.round(
          (value / totalResizablePanelsFractions) * // Percentage of current panel of total resizable panels fractions.
            (100 - totalNonResizablePanelsFractions) * // Space available to distribute between resizable panels.
            100,
        ) / 100;
    }
    // If the total fractions converted to px are smaller than the root width, the configured
    // panels don't take up all the available space (e.g. 3 panels with 20% preferred width each).
    // In this case, we need to distribute the spare fractions among all resizable panels.
  } else if (totalFractions < 100) {
    const spareFractions = 100 - totalFractions;
    for (const [key, { value, unit }] of Object.entries(initialPanelSizes)) {
      // Skip panels that are not resizable (unit other than fr)
      if (unit !== 'fr') {
        continue;
      }

      const percentageOfTotalResizablePanelsFractions =
        Math.round((100 / totalResizablePanelsFractions) * value * 100) / 100;
      // Add percentage spare fractions to the initial panel size.
      initialPanelSizes[key].value +=
        Math.round(percentageOfTotalResizablePanelsFractions * spareFractions) /
        100;
    }
  }

  const gridTemplateColumns = generateGridTemplateColumns(
    panels,
    resizeHandles,
    initialPanelSizes,
  );

  const gridTemplateStyles: CSSProperties = {
    gridTemplateAreas: templateAreas,
    gridTemplateColumns,
  };

  return {
    gridTemplateStyles,
    initialPanelSizes,
  };
}

/**
 * Try to calculate new panel sizes, applying the given delta.
 * If the panels can't be resized by the given amount, returns undefined.
 */
export function applyDelta(
  sizes: PanelSizesWithUnit,
  sortedPanels: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  delta: number,
  rootWidth: number,
  initialPanelIndex: number,
  direction: 1 | -1 = 1,
  loop?: boolean,
  conditionallyRendered?: 'added' | 'dismissed',
): PanelSizesWithUnit | undefined {
  if (delta === 0) {
    return;
  }

  let resizePanelIndex = initialPanelIndex;
  let initialLoop = true;
  let triedAllPanels = false;
  let panelOffset = delta;
  // The amount of fractions that panels have been shrunken to make space for the growing panel.
  // If there is not enough space to shrink existing panels, we try to set the growing panel to
  // its minWidth. If it still doesn't fit, the container will have a horizontal scroll.
  let appliedOffset = 0;
  let newSizes = { ...sizes };
  // When dismissing a panel, only the space previously occupied by that panel needs to be distributed among other panels,
  // so the total offset is exactly that. When resizing a panel, one panel needs to grow and other's need to shrink to
  // make space for the growing panel. So the total offset is twice the delta.
  let totalOffset =
    Math.abs(delta) * (conditionallyRendered === 'dismissed' ? 1 : 2);

  const resizablePanels = sortedPanels.filter(
    (panel) => panel.resizable === true,
  ).length;

  while ((loop && !triedAllPanels) || (!loop && resizePanelIndex !== -1)) {
    // If the current panel index matches the initial panel index, we tried to resize all panels
    // but the expanding panel still doesn't fit. In this case, if we are adding a panel, we try to
    // shrink the added panel down so all the panels fit again.
    triedAllPanels = !initialLoop && resizePanelIndex === initialPanelIndex;

    if (triedAllPanels) {
      panelOffset = appliedOffset - totalOffset;
    }

    // The panel currently being adjusted.
    const currentPanel = sortedPanels[resizePanelIndex];
    // When resizing or adding a panel, the first panel being adjusted is always growing (expanded to its restore size),
    // every other panel is shrinking. Dismissing a panel vice-versa.
    const offsetDirection =
      conditionallyRendered === 'dismissed' ||
      resizePanelIndex === initialPanelIndex
        ? 1
        : -1;
    const currentSize =
      (conditionallyRendered === 'added'
        ? newSizes[currentPanel.name].value
        : sizes[currentPanel.name].value) || 0;
    // The new target size of the current panel.
    const newSize = Math.max(
      0,
      Math.round((currentSize + panelOffset * offsetDirection) * 100) / 100,
    );

    const minWidth = currentPanel.minWidth || 0;
    const isMinWidth =
      minWidth >= fractionsToPx(newSize, rootWidth, sortedResizeHandles.length);

    // If the calculated pixel width after applying the new size is smaller
    // than the current panel's minWidth, we resize panel by whatever is possible.
    // The remaining fractions will be subtracted from the following panels if possible.
    if (isMinWidth) {
      let minWidthInFractions = pxToFractions(
        minWidth,
        rootWidth,
        sortedResizeHandles.length,
      );
      // If a panel has been just added and would be below its minWidth, the calculated
      // width in fractions is the one we should shrink other panels by instead of the delta
      // we initially used. The total offset and panel offset (amount of fractions the next
      // panel will be adjusted by) also need to be adjusted to reflect that.
      if (
        conditionallyRendered === 'added' &&
        resizePanelIndex === initialPanelIndex
      ) {
        // The resize handle width needs to be accounted for when shrinking other panels, so
        // we have to add it here to the minWidth. It will be subtracted again in the addPanel function.
        minWidthInFractions += pxToFractions(RESIZE_HANDLE_SIZE, rootWidth, 0);
        newSizes = {
          ...newSizes,
          [currentPanel.name]: { value: minWidthInFractions, unit: 'fr' },
        };

        totalOffset = minWidthInFractions * 2;
        appliedOffset = minWidthInFractions;
        panelOffset = minWidthInFractions;
      } else {
        // This is the amount of fractions that we could actually shrink the current panel.
        const resizeDelta =
          Math.round(
            (sizes[currentPanel.name].value - minWidthInFractions) * 100,
          ) / 100;
        newSizes = {
          ...newSizes,
          [currentPanel.name]: {
            value:
              resizeDelta < 0
                ? // When adding a panel, we might have to increase the fractions to keep the minWidth (due to the additional resize handle).
                  // In this case, we add the resize delta to the current size instead of applying minWidthInFractions.
                  sizes[currentPanel.name].value - resizeDelta // delta < 0 -> subtract
                : minWidthInFractions,
            unit: 'fr',
          },
        };

        appliedOffset += resizeDelta;
        panelOffset -= resizeDelta;
      }
      // If the panel is not below its minWidth, we can safely set the panel's size to the new size.
    } else {
      appliedOffset += Math.abs(panelOffset);
      newSizes = {
        ...newSizes,
        [currentPanel.name]: { value: newSize, unit: 'fr' },
      };
    }

    // Only apply the new sizes if two panels have been adjusted.
    // Else, we would e.g. only grow one panel even though none of the others
    // has space to shrink, which would lead to incorrect sizes.
    if (
      appliedOffset.toFixed(PRECISION) === totalOffset.toFixed(PRECISION) ||
      (conditionallyRendered === 'added' && triedAllPanels)
    ) {
      return newSizes;
    }

    resizePanelIndex = getNextPanelIndex(sortedPanels, resizePanelIndex, {
      direction,
      loop,
      skipNonResizable:
        conditionallyRendered !== undefined && resizablePanels < 2
          ? false
          : true,
    });

    initialLoop = false;

    // If a panel is added, we may be able to shrink the added panel in order to fit
    // all panels. In this case, we are not ending the loop just yet, but try the
    // added panel again.
    // If a panel is dismissed or panels are resized, we can already end the loop once
    // the `resizePanelIndex` is equal to the `initialPanelIndex`.
    if (conditionallyRendered !== 'added') {
      // If the current panel index matches the initial panel index, we tried to resize all panels
      // but the expanding panel still doesn't fit.
      triedAllPanels = resizePanelIndex === initialPanelIndex;
    }
  }
}

/**
 * Resize all panels by the given delta and adjust the ParentPanel's template columns
 * to reflect the changes.
 * @returns The adjusted panel sizes and the corresponding grid template columns.
 */
export function resizePanels(
  handleIndex: number,
  sortedPanels: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  sizes: Record<string, ParsedValue>,
  deltaX: number,
  rootWidth: number,
): { gridTemplateColumns: string; newSizes: PanelSizesWithUnit } | undefined {
  // When dragging to the left (negative deltaX), we start resizing at the panel
  // right after the resize handle, else, we start right before, ignoring collapsed panels.
  let initialPanelIndex = deltaX < 0 ? handleIndex + 1 : handleIndex;
  while (!sortedPanels[initialPanelIndex].resizable) {
    initialPanelIndex = deltaX < 0 ? ++initialPanelIndex : --initialPanelIndex;

    if (initialPanelIndex < 0 || initialPanelIndex === sortedPanels.length) {
      return;
    }
  }

  // Convert delta in pixels to fractions the panels should be resized by.
  // Uses the abs calculation to prevent rounding issues for the minor dragging changes
  const offset = Math.abs((100 / rootWidth) * deltaX);

  const newSizes = applyDelta(
    sizes,
    sortedPanels,
    sortedResizeHandles,
    offset,
    rootWidth,
    initialPanelIndex,
    deltaX < 0 ? -1 : 1,
  );

  let gridTemplateColumns: string | undefined;
  if (newSizes) {
    // Generate the new template columns with the adjusted sizes.
    gridTemplateColumns = generateGridTemplateColumns(
      sortedPanels,
      sortedResizeHandles,
      newSizes,
    );
  }

  if (gridTemplateColumns === undefined || newSizes === undefined) {
    return;
  }

  return {
    gridTemplateColumns,
    newSizes,
  };
}
